import { createReducer, on } from '@ngrx/store';
import { BackJobsSummaryDto } from '../../../../api/gse/models/back-jobs-summary-dto';
import { JobCardDto } from '../../../../api/gse/models/job-card-dto';
import { MaintenanceAlertDtoResponse } from '../../../../api/gse/models/maintenance-alert-dto-response';
import { MaintenanceDashboardSummaryDto } from '../../../../api/gse/models/maintenance-dashboard-summary-dto';
import { MaintenanceScheduleMinimalDto } from '../../../../api/gse/models/maintenance-schedule-minimal-dto';
import { InspectorDashboardSummaryDto } from '../../../../api/gse/models/inspector-dashboard-summary-dto';
import { OverdueJobsSummaryDto } from '../../../../api/gse/models/overdue-jobs-summary-dto';
import { MaintenanceWeeklyOverviewDto } from '../../../../api/gse/models/maintenance-weekly-overview-dto';
import { MaintenanceOverviewCountResponseDto } from '../../../../api/gse/models/maintenance-overview-count-response-dto';
import { SupervisorDashboardSummaryDto } from '../../../../api/gse/models/supervisor-dashboard-summary-dto';
import { TechnicianEfficiencyDto } from '../../../../api/gse/models/technician-efficiency-dto';
import { JobCardMinimalDtoWithEquipment } from '../../../../api/gse/models/job-card-minimal-dto-with-equipment';
import * as MaintenanceDashboardActions from './maintenance-dashboard.actions';

export interface MaintenanceDashboardState {
  plannerSummary: MaintenanceDashboardSummaryDto | null;
  plannerSummaryLoading: boolean;
  plannerSummaryError: string | null;

  supervisorSummary: SupervisorDashboardSummaryDto | null;
  supervisorSummaryLoading: boolean;
  supervisorSummaryError: string | null;

  maintenanceOverview: MaintenanceOverviewCountResponseDto | null;
  maintenanceOverviewLoading: boolean;
  maintenanceOverviewError: string | null;

  supervisorJobCards: JobCardDto[];
  supervisorJobCardsLoading: boolean;
  supervisorJobCardsError: string | null;

  dailyCheckins: MaintenanceScheduleMinimalDto[];
  dailyCheckinsLoading: boolean;
  dailyCheckinsError: string | null;
  inspectorSummary: InspectorDashboardSummaryDto | null;
  inspectorSummaryLoading: boolean;
  inspectorSummaryError: string | null;

  overdueJobsSummary: OverdueJobsSummaryDto | null;
  overdueJobsSummaryLoading: boolean;
  overdueJobsSummaryError: string | null;

  inspectorJobCards: JobCardDto[];
  inspectorJobCardsLoading: boolean;
  inspectorJobCardsError: string | null;

  overdueJobCards: JobCardDto[];
  overdueJobCardsPage: number;
  overdueJobCardsTotalPages: number;
  overdueJobCardsLoading: boolean;
  overdueJobCardsError: string | null;

  alertsCritical: MaintenanceAlertDtoResponse[];
  alertsUpcoming: MaintenanceAlertDtoResponse[];
  alertsCriticalHasMore: boolean;
  alertsUpcomingHasMore: boolean;
  alertsLoading: boolean;
  alertsError: string | null;

  weeklyOverview: MaintenanceWeeklyOverviewDto[];
  weeklyOverviewLoading: boolean;
  weeklyOverviewError: string | null;

  backJobsSummary: BackJobsSummaryDto | null;
  backJobsSummaryLoading: boolean;
  backJobsSummaryError: string | null;

  technicianEfficiency: TechnicianEfficiencyDto[];
  technicianEfficiencyLoading: boolean;
  technicianEfficiencyError: string | null;

  waitingSpareParts: JobCardMinimalDtoWithEquipment[];
  waitingSparePartsTotal: number;
  waitingSparePartsLoading: boolean;
  waitingSparePartsError: string | null;
}

export const initialState: MaintenanceDashboardState = {
  plannerSummary: null,
  plannerSummaryLoading: false,
  plannerSummaryError: null,

  supervisorSummary: null,
  supervisorSummaryLoading: false,
  supervisorSummaryError: null,

  maintenanceOverview: null,
  maintenanceOverviewLoading: false,
  maintenanceOverviewError: null,

  supervisorJobCards: [],
  supervisorJobCardsLoading: false,
  supervisorJobCardsError: null,

  dailyCheckins: [],
  dailyCheckinsLoading: false,
  dailyCheckinsError: null,
  inspectorSummary: null,
  inspectorSummaryLoading: false,
  inspectorSummaryError: null,

  overdueJobsSummary: null,
  overdueJobsSummaryLoading: false,
  overdueJobsSummaryError: null,

  inspectorJobCards: [],
  inspectorJobCardsLoading: false,
  inspectorJobCardsError: null,

  overdueJobCards: [],
  overdueJobCardsPage: 0,
  overdueJobCardsTotalPages: 1,
  overdueJobCardsLoading: false,
  overdueJobCardsError: null,

  alertsCritical: [],
  alertsUpcoming: [],
  alertsCriticalHasMore: true,
  alertsUpcomingHasMore: true,
  alertsLoading: false,
  alertsError: null,

  weeklyOverview: [],
  weeklyOverviewLoading: false,
  weeklyOverviewError: null,

  backJobsSummary: null,
  backJobsSummaryLoading: false,
  backJobsSummaryError: null,

  technicianEfficiency: [],
  technicianEfficiencyLoading: false,
  technicianEfficiencyError: null,

  waitingSpareParts: [],
  waitingSparePartsTotal: 0,
  waitingSparePartsLoading: false,
  waitingSparePartsError: null,
};

export const maintenanceDashboardReducer = createReducer(
  initialState,
  on(MaintenanceDashboardActions.loadPlannerSummary, (state) => ({
    ...state,
    plannerSummaryLoading: true,
    plannerSummaryError: null,
  })),
  on(MaintenanceDashboardActions.loadPlannerSummarySuccess, (state, { summary }) => ({
    ...state,
    plannerSummary: summary,
    plannerSummaryLoading: false,
  })),
  on(MaintenanceDashboardActions.loadPlannerSummaryFailure, (state, { error }) => ({
    ...state,
    plannerSummaryLoading: false,
    plannerSummaryError: error,
  })),

  on(MaintenanceDashboardActions.loadSupervisorSummary, (state) => ({
    ...state,
    supervisorSummaryLoading: true,
    supervisorSummaryError: null,
  })),
  on(MaintenanceDashboardActions.loadSupervisorSummarySuccess, (state, { summary }) => ({
    ...state,
    supervisorSummary: summary,
    supervisorSummaryLoading: false,
  })),
  on(MaintenanceDashboardActions.loadSupervisorSummaryFailure, (state, { error }) => ({
    ...state,
    supervisorSummaryLoading: false,
    supervisorSummaryError: error,
  })),

  on(MaintenanceDashboardActions.loadMaintenanceOverview, (state) => ({
    ...state,
    maintenanceOverviewLoading: true,
    maintenanceOverviewError: null,
  })),
  on(MaintenanceDashboardActions.loadMaintenanceOverviewSuccess, (state, { summary }) => ({
    ...state,
    maintenanceOverview: summary,
    maintenanceOverviewLoading: false,
  })),
  on(MaintenanceDashboardActions.loadMaintenanceOverviewFailure, (state, { error }) => ({
    ...state,
    maintenanceOverviewLoading: false,
    maintenanceOverviewError: error,
  })),

  on(MaintenanceDashboardActions.loadInspectorSummary, (state) => ({
    ...state,
    inspectorSummaryLoading: true,
    inspectorSummaryError: null,
  })),
  on(MaintenanceDashboardActions.loadInspectorSummarySuccess, (state, { summary }) => ({
    ...state,
    inspectorSummary: summary,
    inspectorSummaryLoading: false,
  })),
  on(MaintenanceDashboardActions.loadInspectorSummaryFailure, (state, { error }) => ({
    ...state,
    inspectorSummaryLoading: false,
    inspectorSummaryError: error,
  })),

  on(MaintenanceDashboardActions.loadOverdueJobsSummary, (state) => ({
    ...state,
    overdueJobsSummaryLoading: true,
    overdueJobsSummaryError: null,
  })),
  on(MaintenanceDashboardActions.loadOverdueJobsSummarySuccess, (state, { summary }) => ({
    ...state,
    overdueJobsSummary: summary,
    overdueJobsSummaryLoading: false,
  })),
  on(MaintenanceDashboardActions.loadOverdueJobsSummaryFailure, (state, { error }) => ({
    ...state,
    overdueJobsSummaryLoading: false,
    overdueJobsSummaryError: error,
  })),

  on(MaintenanceDashboardActions.loadInspectorJobCards, (state) => ({
    ...state,
    inspectorJobCardsLoading: true,
    inspectorJobCardsError: null,
  })),
  on(MaintenanceDashboardActions.loadInspectorJobCardsSuccess, (state, { items }) => ({
    ...state,
    inspectorJobCards: items,
    inspectorJobCardsLoading: false,
  })),
  on(MaintenanceDashboardActions.loadInspectorJobCardsFailure, (state, { error }) => ({
    ...state,
    inspectorJobCardsLoading: false,
    inspectorJobCardsError: error,
  })),

  on(MaintenanceDashboardActions.loadOverdueJobCards, (state) => ({
    ...state,
    overdueJobCardsLoading: true,
    overdueJobCardsError: null,
  })),
  on(MaintenanceDashboardActions.loadOverdueJobCardsSuccess, (state, { items, page, totalPages }) => ({
    ...state,
    overdueJobCards: page > 0 ? [...state.overdueJobCards, ...items] : items,
    overdueJobCardsPage: page,
    overdueJobCardsTotalPages: totalPages,
    overdueJobCardsLoading: false,
  })),
  on(MaintenanceDashboardActions.loadOverdueJobCardsFailure, (state, { error }) => ({
    ...state,
    overdueJobCardsLoading: false,
    overdueJobCardsError: error,
  })),

  on(MaintenanceDashboardActions.loadMaintenanceAlerts, (state, { page = 0, alertType = 'BOTH' }) => ({
    ...state,
    alertsCritical:
      page === 0 && (alertType === 'BOTH' || alertType === 'CRITICAL') ? [] : state.alertsCritical,
    alertsUpcoming:
      page === 0 && (alertType === 'BOTH' || alertType === 'UPCOMING') ? [] : state.alertsUpcoming,
    alertsCriticalHasMore:
      page === 0 && (alertType === 'BOTH' || alertType === 'CRITICAL')
        ? true
        : state.alertsCriticalHasMore,
    alertsUpcomingHasMore:
      page === 0 && (alertType === 'BOTH' || alertType === 'UPCOMING')
        ? true
        : state.alertsUpcomingHasMore,
    alertsLoading: true,
    alertsError: null,
  })),
  on(
    MaintenanceDashboardActions.loadMaintenanceAlertsSuccess,
    (state, { critical, upcoming, page, size, criticalCount, upcomingCount, alertType }) => ({
      ...state,
      alertsCritical:
        alertType === 'UPCOMING'
          ? state.alertsCritical
          : page === 0
            ? critical
            : [...state.alertsCritical, ...critical],
      alertsUpcoming:
        alertType === 'CRITICAL'
          ? state.alertsUpcoming
          : page === 0
            ? upcoming
            : [...state.alertsUpcoming, ...upcoming],
      alertsCriticalHasMore:
        alertType === 'UPCOMING' ? state.alertsCriticalHasMore : criticalCount >= size,
      alertsUpcomingHasMore:
        alertType === 'CRITICAL' ? state.alertsUpcomingHasMore : upcomingCount >= size,
      alertsLoading: false,
    })
  ),
  on(MaintenanceDashboardActions.loadMaintenanceAlertsFailure, (state, { error }) => ({
    ...state,
    alertsLoading: false,
    alertsError: error,
  })),

  on(MaintenanceDashboardActions.loadSupervisorJobCards, (state) => ({
    ...state,
    supervisorJobCardsLoading: true,
    supervisorJobCardsError: null,
  })),
  on(MaintenanceDashboardActions.loadSupervisorJobCardsSuccess, (state, { items }) => ({
    ...state,
    supervisorJobCards: items,
    supervisorJobCardsLoading: false,
  })),
  on(MaintenanceDashboardActions.loadSupervisorJobCardsFailure, (state, { error }) => ({
    ...state,
    supervisorJobCardsLoading: false,
    supervisorJobCardsError: error,
  })),

  on(MaintenanceDashboardActions.loadDailyCheckins, (state) => ({
    ...state,
    dailyCheckinsLoading: true,
    dailyCheckinsError: null,
  })),
  on(MaintenanceDashboardActions.loadDailyCheckinsSuccess, (state, { items }) => ({
    ...state,
    dailyCheckins: items,
    dailyCheckinsLoading: false,
  })),
  on(MaintenanceDashboardActions.loadDailyCheckinsFailure, (state, { error }) => ({
    ...state,
    dailyCheckinsLoading: false,
    dailyCheckinsError: error,
  })),

  on(MaintenanceDashboardActions.loadMaintenanceWeeklyOverview, (state) => ({
    ...state,
    weeklyOverviewLoading: true,
    weeklyOverviewError: null,
  })),
  on(MaintenanceDashboardActions.loadMaintenanceWeeklyOverviewSuccess, (state, { data }) => ({
    ...state,
    weeklyOverview: data,
    weeklyOverviewLoading: false,
  })),
  on(MaintenanceDashboardActions.loadMaintenanceWeeklyOverviewFailure, (state, { error }) => ({
    ...state,
    weeklyOverviewLoading: false,
    weeklyOverviewError: error,
  })),

  on(MaintenanceDashboardActions.loadBackJobsSummary, (state) => ({
    ...state,
    backJobsSummaryLoading: true,
    backJobsSummaryError: null,
  })),
  on(MaintenanceDashboardActions.loadBackJobsSummarySuccess, (state, { summary }) => ({
    ...state,
    backJobsSummary: summary,
    backJobsSummaryLoading: false,
  })),
  on(MaintenanceDashboardActions.loadBackJobsSummaryFailure, (state, { error }) => ({
    ...state,
    backJobsSummaryLoading: false,
    backJobsSummaryError: error,
  })),

  on(MaintenanceDashboardActions.loadTechnicianEfficiency, (state) => ({
    ...state,
    technicianEfficiencyLoading: true,
    technicianEfficiencyError: null,
  })),
  on(MaintenanceDashboardActions.loadTechnicianEfficiencySuccess, (state, { data }) => ({
    ...state,
    technicianEfficiency: data,
    technicianEfficiencyLoading: false,
  })),
  on(MaintenanceDashboardActions.loadTechnicianEfficiencyFailure, (state, { error }) => ({
    ...state,
    technicianEfficiencyLoading: false,
    technicianEfficiencyError: error,
  })),

  on(MaintenanceDashboardActions.loadWaitingForSpares, (state) => ({
    ...state,
    waitingSparePartsLoading: true,
    waitingSparePartsError: null,
  })),
  on(MaintenanceDashboardActions.loadWaitingForSparesSuccess, (state, { items, total, page }) => ({
    ...state,
    waitingSpareParts: page === 0 ? items : [...(state.waitingSpareParts || []), ...items],
    waitingSparePartsTotal: total,
    waitingSparePartsLoading: false,
  })),
  on(MaintenanceDashboardActions.loadWaitingForSparesFailure, (state, { error }) => ({
    ...state,
    waitingSparePartsLoading: false,
    waitingSparePartsError: error,
  }))
);
